import { Button, Card, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useRouter } from 'next/router'
import React from 'react'

import { Wallet } from '../../../types/wallet'
import { urls } from '../../utils/config'
import { calculateAverageRating } from '../../utils/wallet'
import AttributesList from '../AttributesList'
import { MainScore } from '../Review/Rating'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '40px auto',
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: 'none',
    display: 'flex',
    borderRadius: '0',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'visible',
    padding: '30px 50px',
    '@media (max-width: 420px)': {
      margin: '5px',
      padding: '20px',
    },
    '& :hover': {
      cursor: 'pointer',
    },
  },
  mainSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    color: theme.palette.primary.main,
    fontSize: '26px',
    '@media (max-width: 420px)': {
      fontSize: '22px',
    },
  },
  logoContainer: {
    marginTop: '20px',
    marginBottom: '25px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    '@media (max-width: 420px)': {
      width: '100%',
      maxWidth: '100%',
      margin: '30px 0 0 0',
      justifyContent: 'flex-start',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    '@media (max-width: 420px)': {
      justifyContent: 'center',
      flexBasis: '100%',
      marginBottom: '20px',
    },
  },
  logo: {
    maxHeight: '60px',
    width: 'auto',
    maxWidth: '60px',
    objectFit: 'contain',
    alignSelf: 'flex-start',
    marginRight: '25px',
    '@media (max-width: 420px)': {
      alignSelf: 'unset',
      maxHeight: '50px',
    },
  },
  description: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  leftSection: {
    width: '70%',
    '@media (max-width: 420px)': {
      width: '100%',
    },
  },
  rightSection: {
    width: '30%',
    '@media (max-width: 420px)': {
      width: '100%',
    },
  },
  descriptionSection: {
    marginBottom: '30px',
    paddingRight: '30px',
    '@media (max-width: 420px)': {
      padding: '0',
      margin: '20px auto',
      textAlign: 'center',
    },
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginTop: '25px',
    '@media (max-width: 420px)': {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  button: {
    marginTop: '20px',
    width: '204px',
    height: '64px',
    fontSize: '18px',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  mobileButton: {
    marginTop: '20px',
    width: '204px',
    height: '64px',
    fontSize: '18px',
    display: 'none',
    '@media (max-width: 420px)': {
      margin: '20px auto',
      display: 'block',
    },
  },
  campaign: {
    color: theme.palette.primary.main,
    width: 'fit-content',
    position: 'relative',
    '& h5': {
      position: 'relative',
    },
    marginBottom: '20px',
    [theme.breakpoints.down('xs')]: {
      margin: '0 auto',
    },
  },
  campaignTitle: {
    fontSize: '18px',
  },
  campaignBackground: {
    position: 'absolute',
    top: '5px',
    left: '-5px',
    width: '110%',
    backgroundColor: theme.palette.secondary.main,
    height: '26px',
  },
  rating: {
    color: theme.palette.primary.main,
    position: 'relative',
    marginRight: '10px',
    fontSize: '26px',
    '@media (max-width: 420px)': {
      fontSize: '24px',
      marginRight: '5px',
    },
  },
  ratingContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    margin: '0 0 0 auto',
    width: 'fit-content',
    position: 'relative',
    '@media (max-width: 420px)': {
      margin: '20px auto',
      alignItems: 'baseline',
    },
  },
  ratingBackground: {
    position: 'absolute',
    top: '-10px',
    left: '10px',
    width: '150px',
    backgroundColor: theme.palette.secondary.main,
    height: '61px',
    '@media (max-width: 420px)': {
      width: '120px',
      height: '40px',
      top: '2px',
    },
  },
  hideMobile: {
    '@media (max-width: 420px)': {
      display: 'none',
    },
  },
  promotionSection: {
    position: 'relative',
    width: 'fit-content',
    margin: '20px 0',
  },
  promotion: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    position: 'relative',
    '@media (max-width: 420px)': {
      fontSize: '14px',
    },
  },
  promotionBackground: {
    position: 'absolute',
    top: '10px',
    left: '-5px',
    width: '90%',
    backgroundColor: theme.palette.secondary.main,
    height: '18px',
    '@media (max-width: 420px)': {
      top: '8px',
      height: '14px',
    },
  },
}))

interface Props {
  wallet: Wallet
  custodial: boolean
  hideFeature?: boolean
}

function WalletCard({ wallet, custodial, hideFeature }: Props): JSX.Element {
  const classes = useStyles({})
  const { push } = useRouter()
  const url = custodial
    ? `${urls.custodialWallets}/${wallet.path_id}`
    : `${urls.nonCustodialWallets}/${wallet.path_id}`
  const goToReview = () => push(url)
  const averageRating = calculateAverageRating(wallet)
  return (
    <Card className={classes.root} onClick={goToReview}>
      {!hideFeature && wallet.campaign && (
        <div className={classes.campaign}>
          <div className={classes.campaignBackground} />
          <Typography color="primary" variant="h5" className={classes.campaignTitle}>
            {wallet.campaign.title}
          </Typography>
        </div>
      )}
      <div className={classes.mainSection}>
        <div className={classes.logoContainer}>
          <div className={classes.titleContainer}>
            <img
              width={40}
              height={40}
              src={`${wallet.icon && wallet.icon.url}`}
              className={classes.logo}
              alt={`${wallet.title} logo`}
            />
            <Typography variant="h3" className={classes.title}>
              {wallet.title}
            </Typography>
          </div>
          {averageRating && (
            <div className={classes.ratingContainer}>
              <div className={classes.ratingBackground} />
              <Typography variant="h3" className={classes.rating}>
                {averageRating.toFixed(1)}
              </Typography>
              <MainScore value={averageRating.toFixed(1)} color="purple" />
            </div>
          )}
        </div>
        <div className={classes.infoSection}>
          <div className={classes.leftSection}>
            {wallet.short_description && (
              <div className={`${classes.descriptionSection} ${classes.hideMobile}`}>
                <Typography variant="subtitle1">ABOUT</Typography>
                <Typography color="textSecondary" className={classes.description}>
                  {wallet.short_description}
                </Typography>
              </div>
            )}
            {wallet.best_thing && (
              <div className={classes.descriptionSection}>
                <Typography variant="subtitle1">THE BEST THING</Typography>
                <Typography color="textSecondary" className={classes.description}>
                  {wallet.best_thing}
                </Typography>
              </div>
            )}
            {wallet.promotion && (
              <div className={classes.promotionSection}>
                <div className={classes.promotionBackground} />
                <Typography variant="subtitle2" className={classes.promotion}>
                  {wallet.promotion}
                </Typography>
              </div>
            )}
            <Button
              fullWidth={true}
              variant="contained"
              className={classes.button}
              color="primary"
              onClick={goToReview}
            >
              TELL ME MORE
            </Button>
          </div>
          <div className={classes.rightSection}>
            <AttributesList {...wallet} />
            <Button
              fullWidth={true}
              variant="contained"
              className={classes.mobileButton}
              color="primary"
              onClick={goToReview}
            >
              TELL ME MORE
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

export default WalletCard
